/*
 * FWO — "Request a Quote" pop-up for product cards.
 *
 * Quote-only product cards (tag quote-only) render
 * <a data-fwo-quote-popup href="/pages/sb-request-quote?product_handle=…"> — opt-in, so
 * product-page quote buttons (builder checks, build/color params) keep their own flow.
 * Instead of leaving the collection, the click opens a modal with the real
 * quote form, pre-filled from that link's query string:
 *   1. fetch /pages/sb-request-quote?view=embed (templates/page.embed.liquid) for
 *      the page content (<div id="fw-quote-app">) and the country/state options;
 *   2. load quote-form.css/js + fwo-mam-builder.js once (URLs from this script tag);
 *   3. window.FwoQuoteForm.mount(app, search) — same render + prefill as the page,
 *      including the MAM builder pop-up for builder products.
 * Shopify forbids iframing storefront pages (frame-ancestors 'none'), hence the
 * fragment + mount. If anything fails, or the shopper ctrl/cmd-clicks, the link
 * still goes to the full quote page.
 */
(function () {
  "use strict";

  const script = document.currentScript;
  const ASSETS = {
    css: script && script.dataset.quoteCss,
    js: script && script.dataset.quoteJs,
    builder: script && script.dataset.builderJs,
  };

  let modal, body, ready;

  function ensureStyles() {
    if (document.getElementById("fwo-quote-modal-css")) return;
    const css = document.createElement("style");
    css.id = "fwo-quote-modal-css";
    // z-index stays under quote-form.js's MAM builder overlay (10000) so it opens on top.
    css.textContent = `
      .fwo-qm{position:fixed;inset:0;z-index:9990;display:flex;align-items:center;justify-content:center;padding:16px}
      .fwo-qm[hidden]{display:none}
      .fwo-qm__backdrop{position:absolute;inset:0;background:rgba(0,0,0,.55)}
      .fwo-qm__panel{position:relative;width:100%;max-width:820px;max-height:92vh;background:#fff;border-radius:10px;overflow:auto;overscroll-behavior:contain;box-shadow:0 20px 60px rgba(0,0,0,.35)}
      .fwo-qm__body{padding:24px 20px}
      .fwo-qm__close{position:sticky;top:8px;float:right;margin:8px 8px 0 0;z-index:60;width:40px;height:40px;border-radius:50%;border:0;background:rgba(255,255,255,.95);box-shadow:0 1px 6px rgba(0,0,0,.25);font-size:26px;line-height:40px;cursor:pointer;color:#111}
      .fwo-qm__loading{padding:60px 0;text-align:center;color:#555;font-size:15px}
      @media (max-width:600px){.fwo-qm{padding:0}.fwo-qm__panel{max-height:none;height:100%;max-width:none;border-radius:0}.fwo-qm__body{padding:16px}}
      html.fwo-qm-open,html.fwo-qm-open body{overflow:hidden}
    `;
    document.head.appendChild(css);
  }

  function build() {
    ensureStyles();
    modal = document.createElement("div");
    modal.className = "fwo-qm";
    modal.hidden = true;
    modal.setAttribute("role", "dialog");
    modal.setAttribute("aria-modal", "true");
    modal.setAttribute("aria-label", window.FwoT ? window.FwoT("Request a Quote") : "Request a Quote");
    modal.innerHTML =
      '<div class="fwo-qm__backdrop" data-fwo-qm-close></div>' +
      '<div class="fwo-qm__panel">' +
      '<button type="button" class="fwo-qm__close" aria-label="Close" data-fwo-qm-close>&times;</button>' +
      '<div class="fwo-qm__body"></div>' +
      "</div>";
    body = modal.querySelector(".fwo-qm__body");
    modal.addEventListener("click", function (e) {
      if (e.target.closest("[data-fwo-qm-close]")) close();
    });
    document.addEventListener("keydown", function (e) {
      // Leave Escape to the MAM builder overlay while it's open on top.
      if (e.key === "Escape" && modal && !modal.hidden && !document.querySelector(".fwq-mam-overlay")) close();
    });
    document.body.appendChild(modal);
  }

  function loadScript(src) {
    return new Promise(function (resolve, reject) {
      const s = document.createElement("script");
      s.src = src;
      s.onload = resolve;
      s.onerror = reject;
      document.head.appendChild(s);
    });
  }

  // Fetch the fragment + assets once; resolves to the page-content HTML.
  function prepare(pagePath) {
    if (ready) return ready;
    ready = (async function () {
      const res = await fetch(pagePath + "?view=embed", { credentials: "same-origin" });
      if (!res.ok) throw new Error("quote fragment " + res.status);
      const doc = new DOMParser().parseFromString(await res.text(), "text/html");
      const content = doc.getElementById("fwq-embed-content");
      const countries = doc.getElementById("fwq-country-options");
      if (!content || !content.querySelector("#fw-quote-app")) throw new Error("quote fragment: no #fw-quote-app");
      if (countries && !document.getElementById("fwq-country-options")) {
        document.body.appendChild(document.importNode(countries, true));
      }
      if (ASSETS.css && !document.querySelector('link[href="' + ASSETS.css + '"]')) {
        const link = document.createElement("link");
        link.rel = "stylesheet";
        link.href = ASSETS.css;
        document.head.appendChild(link);
      }
      if (ASSETS.builder && !window.FwoMamBuilder) await loadScript(ASSETS.builder);
      if (!window.FwoQuoteForm) await loadScript(ASSETS.js);
      if (!window.FwoQuoteForm) throw new Error("quote form did not load");
      return content.innerHTML;
    })();
    ready.catch(function () { ready = null; });
    return ready;
  }

  // ---- Open / close motion: the pop-up grows out of the button that was pressed and
  // shrinks back into it (like macOS minimize, in reverse). Instant with reduced motion.
  let originRect = null; // the clicked button
  let closing = false;
  const reduceMotion = () => !!(window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches);
  function genie(el, reverse) {
    if (!el || !el.animate || !originRect || reduceMotion()) return null;
    const r = el.getBoundingClientRect();
    const origin = (originRect.left + originRect.width / 2 - r.left) + "px " + (originRect.top + originRect.height / 2 - r.top) + "px";
    const small = { transform: "scale(0.08)", opacity: 0, transformOrigin: origin };
    const full = { transform: "scale(1)", opacity: 1, transformOrigin: origin };
    return el.animate(reverse ? [full, small] : [small, full], reverse
      ? { duration: 260, easing: "cubic-bezier(.4,0,1,1)", fill: "forwards" }
      : { duration: 420, easing: "cubic-bezier(.16,1,.3,1)" });
  }
  function fade(el, reverse, ms) {
    if (!el || !el.animate || reduceMotion()) return null;
    return el.animate(reverse ? [{ opacity: 1 }, { opacity: 0 }] : [{ opacity: 0 }, { opacity: 1 }], { duration: ms, easing: "ease", fill: reverse ? "forwards" : "none" });
  }

  async function open(href) {
    if (!modal) build();
    const url = new URL(href, window.location.origin);
    // "Loading…" only if it takes a moment (usually prefetched → instant, no flicker).
    body.innerHTML = '<div class="fwo-qm__loading" style="visibility:hidden">Loading…</div>';
    const panel = modal.querySelector(".fwo-qm__panel");
    // The panel stays invisible until it has content (or loading takes > 200ms), then grows in.
    panel.style.visibility = "hidden";
    let shown = false;
    const show = function () {
      if (shown || modal.hidden) return;
      shown = true;
      panel.style.visibility = "";
      genie(panel, false);
    };
    setTimeout(function () { const l = body.querySelector(".fwo-qm__loading"); if (l) l.style.visibility = ""; show(); }, 200);
    closing = false;
    modal.hidden = false;
    fade(modal.querySelector(".fwo-qm__backdrop"), false, 250);
    document.documentElement.classList.add("fwo-qm-open");
    modal.querySelector(".fwo-qm__close").focus();
    try {
      const html = await prepare(url.pathname);
      if (modal.hidden) return; // closed while loading
      // Build the form out of sight and reveal it only when the prefill is done — for
      // configurable products that's after the options pop-up has opened, so the
      // customer never sees the form flash up first. (Capped at 8s, just in case.)
      const holder = document.createElement("div");
      holder.hidden = true;
      holder.innerHTML = html;
      body.appendChild(holder);
      await Promise.race([
        Promise.resolve(window.FwoQuoteForm.mount(holder.querySelector("#fw-quote-app"), url.search)).catch(() => {}),
        new Promise((r) => setTimeout(r, 8000)),
      ]);
      if (modal.hidden) { document.querySelectorAll(".fwq-mam-overlay").forEach((o) => o.remove()); return; }
      const loading = body.querySelector(".fwo-qm__loading");
      if (loading) loading.remove();
      holder.hidden = false;
      show();
      // The options / MAM pop-up opened on top grows out of the same button.
      const overlay = document.querySelector(".fwq-mam-overlay");
      if (overlay) { fade(overlay, false, 250); genie(overlay.querySelector(".fwq-mam-modal"), false); }
    } catch (err) {
      console.warn("Quote pop-up failed, opening the quote page instead", err);
      window.location.href = href;
    }
  }

  function close() {
    if (modal.hidden || closing) return;
    closing = true;
    const panel = modal.querySelector(".fwo-qm__panel");
    const backdrop = modal.querySelector(".fwo-qm__backdrop");
    const done = function () {
      [panel, backdrop].forEach(function (el) { el.getAnimations().forEach(function (a) { a.cancel(); }); });
      modal.hidden = true;
      closing = false;
      // quote-form.js looks its elements up by id — clear it so the next card mounts a fresh form.
      body.innerHTML = "";
      document.documentElement.classList.remove("fwo-qm-open");
    };
    const a = genie(panel, true); // shrink back into the button
    fade(backdrop, true, 260);
    if (a) a.finished.then(done, done); else done();
  }

  // Warm-up, so the click opens (almost) instantly: once the page is idle, fetch the
  // form fragment + scripts in the background; when the pointer reaches (or a finger
  // touches) a quote button, start loading that product's data too.
  const idle = (cb) => (window.requestIdleCallback ? window.requestIdleCallback(cb, { timeout: 3000 }) : setTimeout(cb, 1500));
  function quotePagePath(el) {
    const a = el || document.querySelector("a[data-fwo-quote-popup]");
    return a ? new URL(a.getAttribute("href"), window.location.origin).pathname : null;
  }
  function warm() {
    const path = quotePagePath();
    if (path) prepare(path).catch(function () {});
  }
  if (document.readyState === "complete") idle(warm);
  else window.addEventListener("load", function () { idle(warm); });

  function prefetchFor(btn) {
    const url = new URL(btn.getAttribute("href"), window.location.origin);
    const handle = url.searchParams.get("product_handle");
    prepare(url.pathname).then(function () {
      if (handle && window.FwoQuoteForm && window.FwoQuoteForm.prefetch) window.FwoQuoteForm.prefetch(handle);
    }).catch(function () {});
  }
  ["pointerover", "focusin", "touchstart"].forEach(function (type) {
    document.addEventListener(type, function (e) {
      const btn = e.target && e.target.closest && e.target.closest("a[data-fwo-quote-popup]");
      if (btn && !btn.__fwoPrefetched) { btn.__fwoPrefetched = true; prefetchFor(btn); }
    }, { passive: true, capture: true });
  });

  document.addEventListener("click", function (e) {
    const btn = e.target.closest("a[data-fwo-quote-popup]");
    if (!btn || e.defaultPrevented) return;
    if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    e.stopPropagation();
    originRect = btn.getBoundingClientRect();
    open(btn.getAttribute("href"));
  }, true);
})();
